/**
 * Which teams the sector comparison shows.
 *
 * A filter is a list of kart numbers and/or one category. Kart numbers are used
 * rather than team ids because Apex row ids change from one event to the next, so a
 * selection stored by id would be meaningless in the next race while the karts the
 * person cares about are still the same numbers.
 */

export interface SectorFilter {
  /** Karts to show; empty means no restriction by kart. */
  karts: string[];
  /** One category to show, or null for all. */
  category: string | null;
}

/** The parts of a team the filter reads; `TeamInfo` fits. */
export interface FilterableTeam {
  id: string;
  kartNumber: string;
  category: string;
  position: string;
}

export const NO_FILTER: SectorFilter = { karts: [], category: null };

export function isActive(filter: SectorFilter): boolean {
  return filter.karts.length > 0 || filter.category !== null;
}

export function matches(team: FilterableTeam, filter: SectorFilter): boolean {
  if (filter.category !== null && team.category !== filter.category) return false;
  if (filter.karts.length > 0 && !filter.karts.includes(team.kartNumber)) return false;
  return true;
}

export function applyFilter<T extends FilterableTeam>(teams: T[], filter: SectorFilter): T[] {
  return isActive(filter) ? teams.filter((t) => matches(t, filter)) : teams;
}

/** Categories present, in the order they first appear; blanks are left out. */
export function categoriesOf(teams: FilterableTeam[]): string[] {
  const seen: string[] = [];
  for (const team of teams) {
    const category = team.category?.trim();
    if (category && !seen.includes(category)) seen.push(category);
  }
  return seen;
}

function positionOf(team: FilterableTeam): number {
  const n = parseInt(team.position, 10);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

export function toggleKart(filter: SectorFilter, kart: string): SectorFilter {
  const karts = filter.karts.includes(kart)
    ? filter.karts.filter((k) => k !== kart)
    : [...filter.karts, kart];
  return { ...filter, karts };
}

// ── Presets ──────────────────────────────────────────────────────────────────

/** The karts the person has marked in the grid. */
export function markedPreset(teams: FilterableTeam[], markedIds: Set<string>): SectorFilter {
  return {
    karts: teams.filter((t) => markedIds.has(t.id) && t.kartNumber).map((t) => t.kartNumber),
    category: null,
  };
}

/**
 * The first `count` by position at this moment. A snapshot on purpose: the list
 * should not reshuffle underneath someone who is part-way through reading it.
 */
export function topPreset(teams: FilterableTeam[], count: number): SectorFilter {
  const karts = [...teams]
    .filter((t) => t.kartNumber)
    .sort((a, b) => positionOf(a) - positionOf(b))
    .slice(0, count)
    .map((t) => t.kartNumber);
  return { karts, category: null };
}

export function categoryOfPreset(team: FilterableTeam | null): SectorFilter {
  const category = team?.category?.trim();
  return { karts: [], category: category || null };
}

// ── Storage ──────────────────────────────────────────────────────────────────

/** Reads a stored filter, tolerating anything: missing, corrupt, or from another version. */
export function parseStoredFilter(raw: string | null): SectorFilter {
  if (!raw) return NO_FILTER;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null) return NO_FILTER;
    const { karts, category } = value as Record<string, unknown>;
    return {
      karts: Array.isArray(karts)
        ? Array.from(
            new Set(karts.filter((k): k is string => typeof k === 'string' && k.trim() !== '')),
          )
        : [],
      category: typeof category === 'string' && category.trim() !== '' ? category : null,
    };
  } catch {
    return NO_FILTER;
  }
}
